import React, { FC, MutableRefObject, useCallback, useEffect, useMemo, useState } from 'react';
import { useMediaQuery, useTheme } from '@material-ui/core';
import { BlockWithNotes, emptyBlockContent } from '../../../../../config/api/models/Block';
import { ImmersiveReaderButton, WysiwygEditor } from '../../../../shared/components';
import { useUser } from '../../../../shared/hooks';
import { ERROR_MESSAGE_DURATION, PatchStatus, SUCCESS_MESSAGE_DURATION } from '../../../constants';
import { ContentWrapper, RelativeWrapper } from './atoms/Atoms';
import { PatchStatusMessage } from './PatchStatusMessage';
import { BlockFeedbackProps } from '../../../../feedback';
import { useAppDispatch, useAppSelector } from '../../../../shared/hooks/useAppDispatch';
import FroalaEditor from 'react-froala-wysiwyg';
import { patchBlock, toggleBlockIsPrintable } from '../../../../shared/slices/blocks/api/blocks';
import { useGetStudentBookmarkQuery } from 'src/modules/shared/slices/bookmark/api/bookmarkApi';

type WysiwygBlockProps = BlockFeedbackProps & {
  block: BlockWithNotes;
  pageId: string;
  bookId: string;
  readOnly?: boolean;
  hoverActive: boolean;
  forceTitleOnTop?: boolean;
  ref?: Element | undefined;
};

const WysiwygBlock: FC<WysiwygBlockProps> = ({
  block,
  pageId,
  bookId,
  readOnly,
  hoverActive,
  forceTitleOnTop = false,
  ref,
}) => {
  const [patchStatus, setPatchStatus] = useState<PatchStatus>(PatchStatus.INITIAL);
  const theme = useTheme();
  const { user, isStudent, isTeacher } = useUser();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));
  const bookmarkMode = useAppSelector((state) => state.bookmark.bookmarkMode);

  const { data: studentBookmarksData } = useGetStudentBookmarkQuery(bookId as string, {
    skip: !isStudent,
  });
  const bookmark = useMemo(() => {
    if (studentBookmarksData === undefined) return undefined;
    return studentBookmarksData;
  }, [studentBookmarksData]);
  const isBookmarked =
    bookmark?.length && bookmark.filter((item) => item.blockId === block.id).length;
  const dispatch = useAppDispatch();
  const success = useAppSelector((state) => state.blocks.success);
  const error = useAppSelector((state) => state.blocks.error);
  const loading = useAppSelector((state) => state.blocks.loading);
  const [editorRef, setEditorRef] = useState<MutableRefObject<FroalaEditor | null>>();
  const [isEditorReadOnly, setIsEditorReadOnly] = useState(readOnly);

  const requestPatchBlock = useCallback(() => {
    dispatch(
      patchBlock({
        bookId,
        pageId,
        blockId: block.id,
        payload: {
          content: editorRef?.current?.getEditor().el.innerHTML,
        },
      })
    );
  }, [block.id, bookId, dispatch, pageId, editorRef]);

  useEffect(() => {
    if (!isEditorReadOnly && readOnly) {
      requestPatchBlock();
    } else {
      setIsEditorReadOnly(readOnly);
    }

    return () => {};
  }, [readOnly, isEditorReadOnly, requestPatchBlock]);

  useEffect(() => {
    if (!loading && success && readOnly && !isEditorReadOnly) {
      setIsEditorReadOnly(readOnly);
    }
  }, [loading, success, readOnly, isEditorReadOnly]);

  useEffect(() => {
    !readOnly && error && setPatchStatus(PatchStatus.ERROR);
    !readOnly && success && setPatchStatus(PatchStatus.SUCCESS);
  }, [success, error, readOnly]);

  useEffect(() => {
    switch (patchStatus) {
      case PatchStatus.SUCCESS:
        setTimeout(() => setPatchStatus(PatchStatus.INITIAL), SUCCESS_MESSAGE_DURATION);
        break;
      case PatchStatus.ERROR:
        setTimeout(() => setPatchStatus(PatchStatus.INITIAL), ERROR_MESSAGE_DURATION);
        break;
    }
  }, [patchStatus]);

  const handlePrintableChange = useCallback(() => {
    dispatch(
      toggleBlockIsPrintable({
        bookId,
        pageId,
        blockId: block.id,
      })
    );
  }, [dispatch, block.id, bookId, pageId]);

  return (
    <RelativeWrapper
      className={block.content === emptyBlockContent ? 'is-empty' : undefined}
      ref={ref as any}
    >
      <PatchStatusMessage hoverActive={hoverActive} title={block.title} status={patchStatus} />
      <ContentWrapper isMobile={isMobile} bookmarkMode={bookmarkMode} isBookmarked={isBookmarked}>
        <WysiwygEditor
          id={`wysiwyg_${block.id}`}
          value={(block.content as string) || emptyBlockContent}
          readOnly={isEditorReadOnly}
          forceTitleOnTop={forceTitleOnTop}
          onLoadRef={setEditorRef}
          onPrintableChange={handlePrintableChange}
          isPrintable={block.is_printable}
          className={block.is_printable ? undefined : 'hide-on-print'}
        />
        {(isStudent || isTeacher) && user.hasImmersiveReaderEnabled && !!block.content && (
          <ImmersiveReaderButton content={block.content as string} />
        )}
      </ContentWrapper>
    </RelativeWrapper>
  );
};

export default WysiwygBlock;
